import React, { useState, useEffect, useMemo } from 'react';
import { Chart as ChartJS } from 'chart.js/auto';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { enUS } from 'date-fns/locale';
import { COMMIT, MetricNames, MetricCPU, MetricWaits, MetricExceptions, MetricMemory, MetricSqlCount, MetricSqlDuration, MetricApiCpu, MetricApiDuration, MetricULS, DEPLOYMENT, FLIGHT, KILLSWITCH } from '../utils/constants';
import { ulsEventsFromControls, ulsHourlyPoints, ulsLevelNames } from '../utils/ulsMetrics';
import { MessageBar, MessageBarType, PrimaryButton } from '@fluentui/react';
import annotationPlugin from "chartjs-plugin-annotation";
import { LegendButton, ParentButtonsComponent } from './CommonElements.tsx'
import ProfilerIcon from '../Profiler.ico';
import { ProfilePane } from './FlameGraph.tsx';
import { extractTimeSeriesData } from '../utils/flamegraphLauncher.ts';
import {
    formatRepresentationAxisTick,
    formatRepresentationTooltipValue,
    getMissingFunctionRepresentationPoint,
    getRepresentationCoverage,
    getRepresentationLabel,
    getRepresentationUnavailableMessage,
    getRepresentationValue,
} from './commitProfileRepresentations';

const chartAreaGuard = {
    id: 'chartAreaGuard',
    beforeDraw(chart) {
        const area = chart.chartArea;
        if (!area) {
            return false;
        }
        const { left, top, right, bottom, width, height } = area;
        if (![left, top, right, bottom, width, height].every(Number.isFinite)) {
            return false;
        }
    },
};

ChartJS.register(chartAreaGuard);
ChartJS.register(annotationPlugin);

// Component for displaying a graph of commit details
export const CommitDetailsGraph = ({ serviceId, env, process, type, commitDetails, metric, profileDate, representation = 'standard', nav, showBottomButtons = true }) => {
    const [chartInstance, setChartInstance] = useState(null);
    // localProfileDate is used when within the list view panel
    const [localProfileDate, setLocalProfileDate] = useState(null);

    // Clean up tooltip on component mount/unmount
    useEffect(() => {
        deleteTooltip();
        return () => deleteTooltip();
    }, []);

    const details = getGraphData(commitDetails, env, process, type, metric, false);

    // Generates datasets with the received commit details
    const datasets = getDatasets(details, commitDetails, env, type, metric, representation);
    const hasProfileData = datasets.some(dataset => dataset.data.length > 0);
    const hasRepresentedData = datasets.some(dataset =>
        dataset.data.some(point => point.y != null));
    const representationCoverage = getRepresentationCoverage(datasets);
    const representationUnavailableMessage =
        getRepresentationUnavailableMessage(datasets, representation);
    const hasPartialRepresentationCoverage =
        representation !== 'standard'
        && representationCoverage.available > 0
        && representationCoverage.available < representationCoverage.total;

    const handleChartInstance = (chartInstance) => {
        setChartInstance(chartInstance);
    };

    const setProfileDate = nav != null ? (date) => { nav({ profileDate: date }); } : setLocalProfileDate

    // All the datsets that will appear in the graph
    const data = {
        datasets: datasets,
    };

    // Extract time series data for comparison mode
    const timeSeriesData = useMemo(() => {
        if (!chartInstance) return undefined;
        return extractTimeSeriesData(chartInstance);
    }, [chartInstance, datasets]);

    const options = getBasicChartOptions(metric, type, false, setProfileDate, representation);
    const customTooltip = true;// type === COMMIT;

    options.plugins.annotation = { annotations: getAnnotations(commitDetails, env) };

    // messing around with divs to try to avoid shrinking chart
    return (
        <div>
            {!hasProfileData ? (
                <MessageBar messageBarType={MessageBarType.info}>
                    No commit profile data is available for this environment, process, and metric.
                </MessageBar>
            ) : !hasRepresentedData ? (
                <MessageBar messageBarType={MessageBarType.warning}>
                    {representationUnavailableMessage}
                </MessageBar>
            ) : (
                <>
                    {hasPartialRepresentationCoverage && (
                        <MessageBar messageBarType={MessageBarType.warning}>
                            This representation is available for {representationCoverage.available} of {representationCoverage.total} profile dates.
                            {' '}{representationUnavailableMessage}
                        </MessageBar>
                    )}
                    {(type !== COMMIT || metric === MetricExceptions || metric === MetricULS) && chartInstance && <DeselectAllButton chart={chartInstance} />}
                    {customTooltip &&
                        <><div id="tooltipparent"></div>
                            <Line key={`${metric}-${type}-${representation}`} data={data} options={options} ref={handleChartInstance} /></>
                    }
                    {!customTooltip && <Line key={`${metric}-${type}-${representation}`} data={data} options={options} ref={handleChartInstance} />}
                </>
            )}
            {showBottomButtons && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px', position: 'relative' }}>
                    <LegendButton />
                    {metric !== MetricULS && <ParentButtonsComponent serviceId={serviceId} commitDetails={commitDetails} env={env} processId={process} metric={metric} />}
                </div>
            )}
            {metric !== MetricULS && (localProfileDate ?? profileDate) && <ProfilePane mode={'commit'} serviceId={serviceId} commitId={commitDetails.commitId} env={env} process={process} metric={metric} type={type} date={localProfileDate ?? profileDate} isCommitData={true} timeSeriesData={timeSeriesData} enableComparison={true} onclose={() => setProfileDate(null)} />}
            {metric === MetricULS && (
                <div>
                    <p>Hourly UTC occurrences; missing buckets are unavailable, not zero. Activation-overlapping hours are approximate.</p>
                    <p>Counts cover the {Number(process) === 0 ? 'USR' : 'BOT'} role and are attributed to this process as a proxy; they are not filtered by process name.</p>
                    {ulsEventsFromControls(commitDetails.commitControls ?? []).map(event => (
                        <p key={event.eventId}>
                            <strong>{event.eventId}</strong>: {ulsLevelNames[event.level]}, {event.introduced ? 'introduced' : 'existing'}
                            {event.catchContext ? ', catch context' : ''}.
                            {event.message != null ? ` "${event.message}"` : ''}
                            {(commitDetails.commitEvents ?? []).filter(marker => marker.type === 4
                                && marker.identifier === event.eventId && marker.env === Number(env)).map(marker => (
                                <span key={`${marker.farmName}-${marker.startTime}`}>
                                    <br />
                                    First qualifying hour: {new Date(marker.startTime).toISOString()}
                                    {marker.farmName ? `, ${marker.farmName}` : ''}.
                                </span>
                            ))}
                        </p>
                    ))}
                </div>
            )}
        </div>
    );
};

// Button that switches between selecting and deselecting all datasets on the graph
const DeselectAllButton = ({ chart }) => {

    // State to track the text of the button
    const [deselectAllText, setDeselectAllText] = useState('Deselect All');

    const handleDeselectAllClick = () => {
        const datasets = chart.data.datasets;

        // Changes buttons depending on visibility of first dataset
        const visibility = chart.getDatasetMeta(0).hidden;

        // Toggle visibility for all datasets
        for (let i = 0; i < datasets.length; i++) {
            chart.getDatasetMeta(i).hidden = !visibility;
        }

        chart.update();

        if (visibility) {
            setDeselectAllText('Deselect All');
        } else {
            setDeselectAllText('Select All');
        }
    };

    return (
        <div className="flex justify-center">
            <PrimaryButton onClick={handleDeselectAllClick}>{deselectAllText}</PrimaryButton>
        </div>
    );
};

// Fetches CPU graph data for the specified farm and type from the given CommitDetails object
export function getGraphData(commitDetails, env, process, type, metricType, smallGraph = false) {
    const commitMetric = commitDetails.commitMetrics.find(metric => metric.metricType === metricType);

    if (!commitMetric || !commitMetric.farmMetrics) {
        return [];
    }

    if (metricType === MetricULS) {
        const series = commitMetric.farmMetrics.filter(farm => farm.env === env && farm.process === process)
            .flatMap(farm => farm.metricSubTypes ?? []);
        if (!smallGraph) return series;
        const totals = new Map();
        for (const subtype of series)
            for (const point of subtype.commitTimeSeries?.data ?? []) {
                const entry = totals.get(point.date) ?? { total: 0, samples: 0 };
                entry.total += point.metricValue;
                entry.samples++;
                totals.set(point.date, entry);
            }
        return [...totals.entries()].sort(([left], [right]) => left.localeCompare(right))
            .map(([date, entry]) => ({ date, metricValue: entry.samples === series.length ? entry.total : null }));
    }

    const farmMetric = commitMetric.farmMetrics.find(farm => farm.env === env && farm.process === process);
    if (!farmMetric || !farmMetric.metricSubTypes) {
        return [];
    }

    let metricSubType = null;

    if (metricType === MetricCPU || metricType === MetricWaits || metricType === MetricMemory || metricType === MetricApiCpu || metricType == MetricApiDuration) {
        metricSubType = farmMetric.metricSubTypes.find(subType => subType.metricSubType === 'CPU' || subType.metricSubType === 'Memory' || subType.metricSubType === 'All');
    } else if (metricType === MetricExceptions) {
        metricSubType = smallGraph ?
            farmMetric.metricSubTypes.find(subType => subType.metricSubType === 'All') :
            farmMetric.metricSubTypes.filter(subType => subType.metricSubType !== 'All');
    } else if (metricType === MetricSqlCount || metricType === MetricSqlDuration) {
        metricSubType = smallGraph ?
            farmMetric.metricSubTypes.find(subType => subType.metricSubType === 'All') :
            farmMetric.metricSubTypes;
    }

    if (!metricSubType) {
        return [];
    }

    if (type === COMMIT) {
        return ((metricType === MetricExceptions || metricType === MetricSqlCount || metricType === MetricSqlDuration) && !smallGraph) ? metricSubType : (metricSubType.commitTimeSeries?.data ?? []);
    } else {
        return metricSubType;
    }
}

// Generates datasets for the Line chart based on the fetched details and type
export function getDatasets(details, commitDetails, env, type, metric, representation = 'standard', exception) {
    const datasets = [];
    if (metric === MetricULS) {
        const levels = new Map(ulsEventsFromControls(commitDetails.commitControls ?? [])
            .map(event => [event.eventId, ulsLevelNames[event.level]]));
        return details.map(subtype => ({
            label: levels.has(subtype.metricSubType)
                ? `${subtype.metricSubType} (${levels.get(subtype.metricSubType)})`
                : subtype.metricSubType,
            data: ulsHourlyPoints(subtype.commitTimeSeries?.data ?? []),
            spanGaps: false,
            fill: false,
        }));
    }

    const sortDatasetsBySum = (datasetsToSort) => {
        datasetsToSort.sort((datasetA, datasetB) => {
            const sumA = datasetA.data.reduce((total, dataPoint) => total + dataPoint.y, 0);
            const sumB = datasetB.data.reduce((total, dataPoint) => total + dataPoint.y, 0);
            return sumB - sumA; // Sort in descending order
        });
    };

    if (type === COMMIT) {
        if (metric === MetricCPU || metric === MetricWaits || metric === MetricMemory || metric === MetricApiCpu || metric == MetricApiDuration) {
            // Only one dataset
            const dataset = getDatasetFromTimeSeries(null/*aggTimeSeries*/, details, commitDetails, env,
                false/*supportsColor*/, null/*functionId*/, null/*subType*/, false/*isFunctionDataset*/, representation);
            dataset.label = getRepresentationLabel(MetricNames[metric], representation);
            datasets.push(dataset);
        }
        else if (metric === MetricExceptions) {
            const commitDatasets = [];
            details.forEach(obj => {
                const dataset = getDatasetFromTimeSeries(null/*aggTimeSeries*/, obj.commitTimeSeries?.data ?? [], commitDetails, env,
                    false/*supportsColor*/, null/*functionId*/, obj.metricSubType, false/*isFunctionDataset*/, representation);
                dataset.label = obj.metricSubType;
                commitDatasets.push(dataset);
            });
            sortDatasetsBySum(commitDatasets);
            datasets.push(...commitDatasets);
        } else if (metric === MetricSqlCount || metric === MetricSqlDuration) {
            const metricSubType = details.find(subType => subType.metricSubType === 'All')
            if (!metricSubType) {
                return datasets;
            }
            const dataset = getDatasetFromTimeSeries(null/*aggTimeSeries*/, metricSubType.commitTimeSeries?.data ?? [], commitDetails, env,
                false/*supportsColor*/, null/*functionId*/, null/*subType*/, false/*isFunctionDataset*/, representation);
            dataset.label = getRepresentationLabel(MetricNames[metric], representation);
            datasets.push(dataset);
        }
    } else {
        const functionDatasets = [];

        const processFunctionDetails = (aggTimeSeries, functionTimeSeries, commitDetails, env, functionId) => {
            if (functionTimeSeries.length === 0 && aggTimeSeries.length === 0) return;
            const dataset = getDatasetFromTimeSeries(aggTimeSeries, functionTimeSeries, commitDetails, env,
                false/*supportsColor*/, functionId, null/*subType*/, true, representation);
            const fi = commitDetails.functions?.[functionId] ?? {};
            dataset.label = `${fi.className ?? ''}.${fi.methodName ?? ''}`;
            functionDatasets.push(dataset);
        };

        const processDetails = (functionTimeSeries, commitDetails, env, functionId) => {
            if (functionTimeSeries.length === 0) return;
            const dataset = getDatasetFromTimeSeries(null, functionTimeSeries, commitDetails, env,
                false/*supportsColor*/, functionId, null/*subType*/, false/*isFunctionDataset*/, representation);
            const fi = commitDetails.functions?.[functionId] ?? {};
            dataset.label = `${fi.className ?? ''}.${fi.methodName ?? ''}`;
            functionDatasets.push(dataset);
        };

        if (metric === MetricCPU || metric === MetricWaits || metric === MetricMemory || metric === MetricApiCpu || metric == MetricApiDuration) {
            // Loop through details for possible multiple datasets
            const aggTimeSeries = details.commitTimeSeries?.data ?? [];
            const functionIdDetails = details.functionIdDetails ?? {};
            for (const functionId in functionIdDetails) {
                processFunctionDetails(aggTimeSeries, functionIdDetails[functionId]?.functionIdTimeSeries?.data ?? [], commitDetails, env, functionId);
            }
        } else if (metric === MetricExceptions || metric === MetricSqlCount || metric === MetricSqlDuration) {
            /// For Exceptions with Functions
            if (details.length > 0) {
                const exceptionObj = details.find(obj => obj.metricSubType === type);
                if (exceptionObj) {
                    const functionIdDetails = exceptionObj.functionIdDetails ?? {};
                    for (const functionId in functionIdDetails) {
                        processDetails(functionIdDetails[functionId]?.functionIdTimeSeries?.data ?? [], commitDetails, env, functionId);
                    }
                }
            }
        }
        sortDatasetsBySum(functionDatasets);
        datasets.push(...functionDatasets);
    }

    return datasets;
}

export function getDatasetFromTimeSeries(aggTimeSeries, timeSeries, commitDetails, env, supportsColor, functionId, subType, isFunctionDataset, representation = 'standard') {
    const dates = isFunctionDataset ? timeSeries.map(t => t.date) : [];
    const events = commitDetails.commitEvents ? commitDetails.commitEvents.filter(e => e.env === env) : [];
    let isDeployed = false;
    let isActivated = false;
    let isKillswitched = false;
    let nextEvent = 0;
    const data = [];
    const series = isFunctionDataset ? aggTimeSeries : timeSeries;

    for (const p of series) {
        const day = (new Date(p.date)).getDay();
        // skip weekends as load mix is different
        if (day === 0 || day === 6) continue;

        while (nextEvent < events.length && events[nextEvent].startTime <= p.date) {
            const event = events[nextEvent];
            if (event.type === DEPLOYMENT) {
                isDeployed = true;
                if (!commitDetails.commitControls.some(c => c.controlType === FLIGHT)) {
                    //checking if PR doesn't have any control that is a flight
                    isActivated = true;
                }
            }

            if (event.type === KILLSWITCH) {
                isKillswitched = event.action === 1;
            }

            if (event.type === FLIGHT) {
                isActivated = event.action === 1;
            }

            nextEvent++;
        }

        const matchingPoint = isFunctionDataset
            ? (!dates.includes(p.date) ? null : timeSeries.find(ts => ts.date === p.date))
            : p;
        const sourcePoint = matchingPoint
            ?? getMissingFunctionRepresentationPoint(p);
        const value = getRepresentationValue(sourcePoint, representation);
        data.push({
            x: new Date(p.date),
            y: value,
            profileUsage: sourcePoint?.profileUsage ?? null,
            requestCount: sourcePoint?.requestCount ?? null,
            originalDate: p.date.substring(0, 10) + (functionId != null || subType != null ? '/' + (functionId ?? '') : '') + (subType != null ? '/' + subType : ''),
            isDeployed: isDeployed,
            isActivated: isActivated,
            targetColor: isKillswitched ? 'red' : (isActivated ? 'blue' : (isDeployed ? 'black' : 'grey')),
        });
    }

    const segment = supportsColor ? {
        borderColor: ctx => getLineColor(ctx),
        borderDash: ctx => getDashStyle(ctx),
    } : {
        borderDash: ctx => getDashStyleFunctions(ctx),
    }

    return {
        data: data,
        fill: false,
        segment: segment
    }
}

export const lineStyleNotDeployed = [6, 12];
export const lineStyleNotActivated = [2, 1];

export function getLineColor(ctx) {
    if (ctx.p1 && ctx.p1.raw) {
        return ctx.p1.raw.targetColor;
    }
    return undefined;
}
export function getDashStyle(ctx) {

    if (ctx.p1 && ctx.p1.raw && !ctx.p1.raw.isDeployed) {
        return [6, 6];
    }
    return undefined;
}
export function getDashStyleFunctions(ctx) {

    if (ctx.p1 && ctx.p1.raw) {
        if (!ctx.p1.raw.isDeployed) return lineStyleNotDeployed;
        if (!ctx.p1.raw.isActivated) return lineStyleNotActivated;
    }
    return undefined;
}

export function getAnnotations(commitDetails, env) {
    const annotations = [];

    annotations.push({
        xMin: new Date(commitDetails.commitMetaData.prMergeDate),
        borderColor: "grey",
        label: { content: "Check-in" }
    });


    let deployed = false;
    for (const evt of commitDetails.commitEvents) {
        if (evt.env === env) {
            const a = {
                xMin: new Date(evt.startTime),
                label: {},
            }
            switch (evt.type) {
                case 0:
                    a.label.content = "Killswitch " + evt.identifier + (evt.action === 1 ? ' activated' : ' deactivated');
                    a.borderColor = 'red';
                    if (evt.action === 0) {
                        a.borderDash = [3, 3];
                    }
                    break;
                case 1:
                    a.label.content = "Flight " + evt.identifier + (evt.action === 1 ? ' on' : ' off');
                    a.borderColor = 'green';
                    if (evt.action === 0) {
                        a.borderDash = [3, 3];
                    }
                    break
                case 2:
                case 3:
                    if (deployed) continue;
                    a.label.content = "Deploying " + evt.identifier;
                    a.borderColor = 'grey';
                    deployed = true;
                    break
                default:
                    continue;
            }
            annotations.push(a);
        }
    }
    for (const a of annotations) {
        a.xMax = a.xMin;
        a.type = "line";
        a.mode = "vertical";
        a.xScaleID = "x";
        a.label.display = true;
        a.label.rotation = 90;
        a.label.backgroundColor = 'rgba(0, 0, 0, 0.5)';
        a.label.position = '90%';
    }
    return annotations;
}
export function getBasicChartOptions(metric, type, fromGraphView, tooltipSelect, representation = 'standard') {
    //g_tooltipSelect = tooltipSelect;
    window.g_tooltipSelect = tooltipSelect //() => { alert('hi') }
    const formatTooltipLabel = (context) => {
        const label = context.dataset.label || '';
        const value = formatRepresentationTooltipValue(
            context.parsed.y,
            context.formattedValue,
            representation);
        return label ? `${label}: ${value}` : value;
    };
    let options = {
        responsive: true,
        maintainAspectRatio: true,
        interaction: {
            intersect: false,
            mode: 'index',
        },
        // events: fromGraphView ? null : ['mousemove', 'click', 'touchstart', 'touchmove'],

        plugins: {
            tooltipXXX: {
                // mode: 'nearest',
                // intersect: true,
                callbacks: {

                    // Appears when a point on the graph is hovered over
                    label: function (context) {
                        const label = context.dataset.label || ''; // Get the dataset label
                        const value = context.parsed.y || 0; // Get the y-axis value
                        return label + ': ' + value;
                    },
                },
            },
        },
        scales: {
            x: {
                type: 'time',
                adapters: {
                    date: {
                        locale: enUS,
                    },
                },
                time: {
                    unit: 'day',
                    displayFormats: {
                        day: 'MM-dd', // Format for displaying date and time
                    },
                },
                title: {
                    display: true,
                    text: 'Time', // Label for x-axis
                },
            },
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: representation === 'profileUsage'
                        ? 'Profile usage'
                        : representation === 'normalizedProfileUsage'
                            ? 'Normalized profile usage'
                            : MetricNames[metric],
                },
                ticks: {
                    maxTicksLimit: representation === 'normalizedProfileUsage'
                        ? 8
                        : undefined,
                    callback: function (value) {
                        const scaleMaximum = Math.max(
                            Math.abs(Number(this.min)),
                            Math.abs(Number(this.max)));
                        return formatRepresentationAxisTick(
                            Number(value),
                            scaleMaximum,
                            representation);
                    },
                },
            },
        },
    };

    if (fromGraphView) {
        options.events = ['mousemove', 'click', 'touchstart', 'touchmove', 'mouseout'];
        options.plugins.tooltip = {
            mode: fromGraphView ? 'index' : 'nearest',
            axis: 'x',
            intersect: false,
            callbacks: {
                label: formatTooltipLabel,
            },
        }

        if (!fromGraphView) {
            options.interaction = undefined;
            options.plugins.tooltip.intersect = true;
            options.plugins.tooltip.axis = undefined;
        }
    }
    else {
        options.events = ['mousemove', 'click', 'touchstart', 'touchmove'];
        options.plugins.tooltip = {
            // Disable the on-canvas tooltip
            enabled: false,
            mode: type === COMMIT ? 'index' : 'nearest',
            intersect: type === COMMIT ? false : false,
            axis: type === COMMIT ? 'xy' : 'xy',

            backgroundColor: '#227799',
            //events: ['click'],
            events: ['mousemove', 'click', 'touchstart', 'touchmove'],
            randomstuff: 1,
            callbacks: {
                label: formatTooltipLabel,
            },

            external: externalTooltipHandler
        }
    }
    if (metric === MetricULS) {
        options.scales.x.time.unit = 'hour';
        options.scales.x.time.displayFormats.hour = 'MM-dd HH:mm';
        options.scales.x.title.text = 'Time (UTC; hover for exact hour)';
        options.scales.x.ticks = {
            callback: value => new Date(value).toISOString().substring(5, 16).replace('T', ' '),
        };
        options.scales.y.title.text = 'ULS occurrences/hour';
        options.scales.y.ticks.callback = value => Number(value).toLocaleString('en-US');
        options.plugins.tooltip = {
            callbacks: {
                title: contexts => contexts.length ? new Date(contexts[0].parsed.x).toISOString() : '',
                label: context => `${context.dataset.label}: ${context.parsed.y.toLocaleString('en-US')} events/hour`,
            },
        };
        options.plugins.legend = { display: true };
        return options;
    }
    if (type === COMMIT) {
        if (metric !== MetricExceptions) {
            options.plugins.legend = { display: false };
        }
    }

    return options;
}

const getOrCreateTooltip = (chart) => {
    let tooltipEl = document.getElementById('tooltipparent').querySelector('div')

    if (!tooltipEl) {
        tooltipEl = document.createElement('div');
        //tooltipEl.id = 'chartjs-tooltip';
        tooltipEl.style.background = 'rgba(0, 0, 0, 0.7)';
        tooltipEl.style.borderRadius = '3px';
        tooltipEl.style.color = 'white';
        tooltipEl.style.opacity = 1;
        //tooltipEl.style.pointerEvents = 'none';
        tooltipEl.style.position = 'absolute';
        //tooltipEl.style.transform = 'translate(-50%, 0)';
        tooltipEl.style.transition = 'all .1s ease';

        const table = document.createElement('table');
        table.style.margin = '0px';

        tooltipEl.appendChild(table);
        document.getElementById('tooltipparent').appendChild(tooltipEl);
    }

    return tooltipEl;
};

function deleteTooltip() {
    const parent = document.getElementById('tooltipparent');
    if (parent != null && parent.hasChildNodes()) {
        parent.removeChild(parent.firstChild);
    }
}

function externalTooltipHandler(context) {
    // Tooltip Element
    const { chart, tooltip } = context;
    const tooltipEl = getOrCreateTooltip(chart);

    // Hide if no tooltip
    if (tooltip.opacity === 0) {
        tooltipEl.style.opacity = 0;
        return;
    }

    // Set Text
    if (tooltip.body) {
        const titleLines = tooltip.title || [];
        const bodyLines = tooltip.body.map(b => b.lines);

        const tableHead = document.createElement('thead');

        titleLines.forEach(title => {
            const tr = document.createElement('tr');
            tr.style.borderWidth = 0;

            const th = document.createElement('th');
            th.style.borderWidth = 0;
            const text = document.createTextNode(title);

            th.appendChild(text);
            tr.appendChild(th);
            tableHead.appendChild(tr);
        });

        const tableBody = document.createElement('tbody');
        bodyLines.forEach((body, i) => {
            const colors = tooltip.labelColors[i];

            const span = document.createElement('span');
            span.style.background = colors.backgroundColor;
            span.style.borderColor = colors.borderColor;
            span.style.borderWidth = '2px';
            span.style.marginRight = '10px';
            span.style.height = '10px';
            span.style.width = '10px';
            span.style.display = 'inline-block';

            const tr = document.createElement('tr');
            tr.style.backgroundColor = 'inherit';
            tr.style.borderWidth = 0;

            const td = document.createElement('td');
            td.style.borderWidth = 0;


            const datapoint = tooltip.dataPoints[i];
            const date = datapoint.dataset.data[datapoint.dataIndex].originalDate;
            td.innerHTML = '<a href="#" onclick="g_tooltipSelect(this.dataset.date);return false;" class="profilerLink" data-date="' + date + '" title="View flame graph"><img src="' + ProfilerIcon + '" height="16" width="16"/></a>'
                + '<span> ' + body + '</span>';
            tr.appendChild(td);
            tableBody.appendChild(tr);
        });

        const tableRoot = tooltipEl.querySelector('table');

        // Remove old children
        while (tableRoot.firstChild) {
            tableRoot.firstChild.remove();
        }

        // Add new children
        tableRoot.appendChild(tableHead);
        tableRoot.appendChild(tableBody);
    }

    const canvasPosition = chart.canvas.getBoundingClientRect();
    const tooltipParent = document.getElementById('tooltipparent');
    const parentPosition = tooltipParent ? tooltipParent.getBoundingClientRect() : { left: 0, top: 0 };

    // Display, position, and set styles for font
    tooltipEl.style.opacity = 1;
    tooltipEl.style.left = (canvasPosition.left - parentPosition.left) + tooltip.caretX + 'px';
    tooltipEl.style.top = (canvasPosition.top - parentPosition.top) + tooltip.caretY + 'px';
    tooltipEl.style.font = tooltip.options.bodyFont.string;
    tooltipEl.style.padding = tooltip.options.padding + 'px ' + tooltip.options.padding + 'px';
};
